import { Button, Typography } from '@material-ui/core'
import Skeleton from '@material-ui/lab/Skeleton'
import { makeStyles } from '@material-ui/core/styles'
import CalendarBlankOutlineIcon from 'mdi-react/CalendarBlankOutlineIcon'
import HomeCityOutlineIcon from 'mdi-react/HomeCityOutlineIcon'
import ImageMultipleOutlineIcon from 'mdi-react/ImageMultipleOutlineIcon'
import MapMarkerOutlineIcon from 'mdi-react/MapMarkerOutlineIcon'
import { FC, useState } from 'react'
import { Link, useLocation } from 'react-router-dom'
import { CatalogApartment } from '../../services/catalog'
import CatalogFilters, { applyFilters, useFilters } from './CatalogFilters'
import { availability, formatEuro, listingTitle, locationLine } from './format'
import PhotoPlaceholder from './PhotoPlaceholder'
import { useCatalog, useCatalogPage } from './useCatalog'

const NEW_LISTING_MS = 7 * 24 * 60 * 60 * 1000

const useStyles = makeStyles((theme) => ({
  page: {
    maxWidth: 1240,
    margin: '0 auto',
    padding: '40px 24px 80px',
    color: '#1f2421',
    [theme.breakpoints.down('xs')]: {
      padding: '24px 16px 64px'
    }
  },
  hero: {
    marginBottom: 32
  },
  title: {
    fontSize: 40,
    lineHeight: 1.15,
    margin: 0,
    [theme.breakpoints.down('xs')]: {
      fontSize: 30
    }
  },
  lead: {
    marginTop: 12,
    fontSize: 18,
    color: '#5b635e',
    maxWidth: 720
  },
  grid: {
    display: 'grid',
    gridTemplateColumns: 'repeat(auto-fill, minmax(300px, 1fr))',
    gap: 24,
    [theme.breakpoints.down('xs')]: {
      gridTemplateColumns: '1fr',
      gap: 20
    }
  },
  card: {
    display: 'flex',
    flexDirection: 'column',
    background: '#fff',
    borderRadius: 12,
    overflow: 'hidden',
    border: '1px solid #e5e7e6',
    transition: 'box-shadow 150ms ease, transform 150ms ease',
    '&:hover': {
      boxShadow: '0 10px 30px rgba(31,36,33,0.12)',
      transform: 'translateY(-2px)'
    }
  },
  cardLink: {
    color: 'inherit',
    textDecoration: 'none',
    display: 'flex',
    flexDirection: 'column',
    flexGrow: 1
  },
  media: {
    position: 'relative',
    paddingTop: '66.66%',
    background: '#f1f2f4'
  },
  image: {
    position: 'absolute',
    top: 0,
    left: 0,
    width: '100%',
    height: '100%',
    objectFit: 'cover'
  },
  price: {
    position: 'absolute',
    left: 12,
    bottom: 12,
    padding: '6px 12px',
    borderRadius: 8,
    background: '#fff',
    fontFamily: 'CircularBold',
    fontSize: 18,
    boxShadow: '0 2px 8px rgba(0,0,0,0.15)'
  },
  priceUnit: {
    fontFamily: 'CircularRegular',
    fontSize: 13,
    color: '#5b635e'
  },
  photoCount: {
    position: 'absolute',
    right: 12,
    bottom: 12,
    display: 'flex',
    alignItems: 'center',
    gap: 4,
    padding: '4px 8px',
    borderRadius: 999,
    background: 'rgba(0,0,0,0.6)',
    color: '#fff',
    fontSize: 13
  },
  badges: {
    position: 'absolute',
    left: 12,
    top: 12,
    display: 'flex',
    gap: 6
  },
  badge: {
    padding: '4px 10px',
    borderRadius: 999,
    background: theme.palette.primary.main,
    color: '#fff',
    fontFamily: 'CircularBold',
    fontSize: 12,
    letterSpacing: 0.3,
    textTransform: 'uppercase'
  },
  badgeLight: {
    background: '#fff',
    color: '#1f2421'
  },
  body: {
    padding: '16px 18px 4px',
    flexGrow: 1
  },
  cardTitle: {
    fontFamily: 'CircularBold',
    fontSize: 18,
    lineHeight: 1.3,
    margin: 0
  },
  metaRow: {
    display: 'flex',
    alignItems: 'center',
    gap: 6,
    marginTop: 8,
    color: '#5b635e',
    fontSize: 15,
    '& svg': { flex: '0 0 auto', color: '#8a938d' }
  },
  actions: {
    display: 'flex',
    gap: 10,
    padding: '14px 18px 18px'
  },
  action: {
    flex: 1,
    borderRadius: 8,
    textTransform: 'none',
    fontFamily: 'CircularBold',
    fontSize: 15,
    boxShadow: 'none'
  },
  state: {
    textAlign: 'center',
    padding: '64px 16px',
    color: '#5b635e',
    '& svg': { color: '#9aa39c' }
  },
  footnote: {
    marginTop: 40,
    color: '#8a938d',
    fontSize: 14,
    textAlign: 'center'
  }
}))

const CardImage: FC<{ apartment: CatalogApartment }> = ({ apartment }) => {
  const classes = useStyles()
  const [failed, setFailed] = useState(false)
  const src = apartment.images[0]
  if (!src || failed) {
    return <PhotoPlaceholder />
  }
  return (
    <img
      className={classes.image}
      src={src}
      alt={`${listingTitle(apartment)}, ${apartment.address || apartment.municipality}`}
      loading='lazy'
      onError={() => setFailed(true)}
    />
  )
}

const ApartmentCard: FC<{ apartment: CatalogApartment }> = ({ apartment }) => {
  // the listing's back link returns to the same filtered list
  const listing = { pathname: `/homes/${apartment.code}`, state: { catalogSearch: useLocation().search } }
  const classes = useStyles()
  const isNew = !!apartment.publishedAt && Date.now() - apartment.publishedAt < NEW_LISTING_MS
  return (
    <article className={classes.card}>
      <Link to={listing} className={classes.cardLink}>
        <div className={classes.media}>
          <CardImage apartment={apartment} />
          {(isNew || apartment.isFurnished) && (
            <div className={classes.badges}>
              {isNew && <span className={classes.badge}>New</span>}
              {apartment.isFurnished && <span className={`${classes.badge} ${classes.badgeLight}`}>Furnished</span>}
            </div>
          )}
          {apartment.price !== undefined && (
            <span className={classes.price}>
              {formatEuro(apartment.price)} <span className={classes.priceUnit}>/ month</span>
            </span>
          )}
          {apartment.images.length > 1 && (
            <span className={classes.photoCount}>
              <ImageMultipleOutlineIcon size={15} /> {apartment.images.length}
            </span>
          )}
        </div>
        <div className={classes.body}>
          <h2 className={classes.cardTitle}>
            {listingTitle(apartment)}
          </h2>
          <div className={classes.metaRow}>
            <MapMarkerOutlineIcon size={17} />
            <span>{[apartment.address, locationLine(apartment)].filter(Boolean).join(', ')}</span>
          </div>
          <div className={classes.metaRow}>
            <CalendarBlankOutlineIcon size={17} />
            <span>{availability(apartment)}</span>
          </div>
        </div>
      </Link>
      <div className={classes.actions}>
        <Button
          component={Link}
          to={listing}
          variant='outlined'
          color='primary'
          className={classes.action}
        >
          View details
        </Button>
        <Button
          href={`/new-application/${apartment.code}?lang=en`}
          variant='contained'
          color='primary'
          className={classes.action}
        >
          Apply
        </Button>
      </div>
    </article>
  )
}

const LoadingGrid: FC = () => {
  const classes = useStyles()
  return (
    <div className={classes.grid} aria-busy='true'>
      {Array.from({ length: 6 }).map((_, i) => (
        <div key={i} className={classes.card}>
          <Skeleton variant='rect' height={220} />
          <div className={classes.body}>
            <Skeleton width='70%' height={28} />
            <Skeleton width='85%' />
            <Skeleton width='50%' />
          </div>
          <div className={classes.actions}>
            <Skeleton variant='rect' height={40} style={{ flex: 1, borderRadius: 8 }} />
            <Skeleton variant='rect' height={40} style={{ flex: 1, borderRadius: 8 }} />
          </div>
        </div>
      ))}
    </div>
  )
}

/** Municipalities with homes right now, most homes first: "Helsinki, Vantaa and Espoo" */
const municipalityList = (apartments: CatalogApartment[]) => {
  const counts = new Map<string, number>()
  apartments.forEach(({ municipality }) => counts.set(municipality, (counts.get(municipality) || 0) + 1))
  const names = Array.from(counts.entries()).sort((a, b) => b[1] - a[1]).map(([name]) => name)
  return names.length > 1 ? `${names.slice(0, -1).join(', ')} and ${names[names.length - 1]}` : names[0] || 'the Helsinki region'
}

const Catalog: FC = () => {
  const classes = useStyles()
  useCatalogPage()
  const { apartments, error, retry } = useCatalog()
  const [filters, updateFilters, clearFilters] = useFilters()
  const shown = applyFilters(apartments || [], filters)

  const count = apartments?.length
  const lead =
    count === undefined
      ? 'Rental homes across the Helsinki region.'
      : `${count} ${count === 1 ? 'home' : 'homes'} available in ${municipalityList(apartments || [])}. Browse the listings and apply online directly from each home.`

  return (
    <div className={classes.page}>
      <header className={classes.hero}>
        <Typography variant='h1' className={classes.title}>
          Rental homes in the Helsinki region
        </Typography>
        <Typography className={classes.lead}>{lead}</Typography>
      </header>

      {error && (
        <div className={classes.state}>
          <Typography variant='h5' gutterBottom>
            We couldn't load the listings
          </Typography>
          <Typography gutterBottom>Please check your connection and try again.</Typography>
          <Button variant='contained' color='primary' onClick={retry} style={{ marginTop: 12 }}>
            Try again
          </Button>
        </div>
      )}

      {!error && !apartments && <LoadingGrid />}

      {!error && apartments && apartments.length === 0 && (
        <div className={classes.state}>
          <HomeCityOutlineIcon size={48} />
          <Typography variant='h5' gutterBottom>
            No homes available right now
          </Typography>
          <Typography>Please check back soon.</Typography>
        </div>
      )}

      {!error && apartments && apartments.length > 0 && (
        <>
          <CatalogFilters
            apartments={apartments}
            shown={shown.length}
            filters={filters}
            onChange={updateFilters}
            onClear={clearFilters}
          />
          {shown.length > 0 ? (
            <div className={classes.grid}>
              {shown.map((apartment) => (
                <ApartmentCard key={apartment.code} apartment={apartment} />
              ))}
            </div>
          ) : (
            <div className={classes.state}>
              <Typography variant='h5' gutterBottom>
                No homes match these filters
              </Typography>
              <Button variant='contained' color='primary' onClick={clearFilters} style={{ marginTop: 12 }}>
                Clear filters
              </Button>
            </div>
          )}
          <p className={classes.footnote}>Listings are updated in real time. Rents are per month.</p>
        </>
      )}
    </div>
  )
}

export default Catalog
